import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { isStaleChunkError, reloadOnceForStaleChunk } from '../lib/chunkReload';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Unhandled render error:', error, errorInfo);
    // A stale lazy-route chunk after a deploy can surface as a thrown error
    // here instead of main.tsx's vite:preloadError listener, depending on
    // how the failed import propagates - same guard, so only one path
    // actually reloads.
    if (isStaleChunkError(error)) {
      reloadOnceForStaleChunk();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5">Something went wrong.</Typography>
          <Typography variant="body2" color="text.secondary">
            Please refresh the page. If this keeps happening, contact support.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
